import type { ILogger } from '../logger';
import { toIanaTimeZone } from './timeZone';

/**
 * Format a day-of-month number as an ordinal string (e.g., 1 → "1st", 5 → "5th", 22 → "22nd").
 */
export function formatOrdinal(day: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const remainder = day % 100;
  const suffix = (remainder >= 11 && remainder <= 13) ? 'th' : (suffixes[day % 10] ?? 'th');
  return `${day}${suffix}`;
}

// Date formatting utilities for converting UTC ISO strings to local browser time

const defaultLocale = 'en-US';
const dateFormat = new Intl.DateTimeFormat(defaultLocale, {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
});
const timeFormat = new Intl.DateTimeFormat(defaultLocale, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});
const shortDateTimeFormat = new Intl.DateTimeFormat(defaultLocale, {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
});

// A compact, year-less label for a `YYYY-MM-DD` date key. Parsed and formatted in
// UTC so the rendered day never shifts across the viewer's timezone.
const weekdayMonthDayFormat = new Intl.DateTimeFormat(defaultLocale, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

function parseDateKey(dateKey: string): Date {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

/** `"2026-05-11" → "Mon, May 11"`. Input is a `YYYY-MM-DD` date key. */
export function formatDateKeyToWeekdayMonthDay(dateKey: string): string {
  return weekdayMonthDayFormat.format(parseDateKey(dateKey));
}

/** US numeric date-only `MM-DD-YYYY` (UTC) — e.g. for date-range query params. */
export function formatDateToUsNumericDate(date: Date): string {
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${mm}-${dd}-${date.getUTCFullYear()}`;
}

/**
   * Parse UTC ISO string and convert to local Date object
   * @param iso - UTC ISO string (with or without 'Z' suffix)
   * @param logger - Optional logger for error reporting
   * @returns Local Date object or null if parsing fails
   */

export function parseUtcIsoToDate(iso: string, logger?: ILogger): Date | null {
  try {
    // Ensure the string is treated as UTC by adding 'Z' if not present
    const utcIso = iso.endsWith('Z') ? iso : `${iso}Z`;
    const date = new Date(utcIso);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      logger?.warn('Invalid date string:', iso);
      return null;
    }
    return date;
  } catch (error) {
    logger?.warn('Error parsing date:', iso, error);
    return null;
  }
}

/**
* Format UTC ISO string to short US date format (e.g., "Jan 15, 2024")
*/
export function formatDateToShortUS(date: Date): string {
  return dateFormat.format(date);
}

/**
 * Format Date to short US date with time and seconds (e.g., "May 13, 2023, 1:06:03 AM")
 */
export function formatDateToShortUSWithTime(date: Date): string {
  return shortDateTimeFormat.format(date);
}

/** Year-less date and time on `timeZone`'s calendar: `"Aug 3, 12:00 AM"`. */
export function formatDateToMonthDayTime(date: Date | number, timeZone: string): string {
  return new Intl.DateTimeFormat(defaultLocale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(date);
}

/** An ISO instant as `timeZone` reads it: `"Aug 19, 2026, 12:00 AM"`. Unparseable input comes back as is. */
export function formatInstantInTimeZone(iso: string, timeZone: string): string {
  const instant = new Date(iso);
  if (!Number.isFinite(instant.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat(defaultLocale, { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(instant);
}

/**
 * Formats ISO instants as `timeZone` reads them, to the second: `"Oct 7, 2026, 11:59:58 PM"`.
 * A Windows zone id maps to IANA and an unknown one reads as UTC; an unparseable instant formats as `null`.
 */
export function createInstantFormatter(timeZone: string): (iso: string) => string | null {
  const formatter = new Intl.DateTimeFormat(defaultLocale, {
    timeZone: toIanaTimeZone(timeZone) ?? 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
  return iso => {
    const epoch = Date.parse(iso);
    return Number.isFinite(epoch) ? formatter.format(epoch) : null;
  };
}

/** Below this, treat as the backend's `DateTime.MinValue` ("no date") sentinel, not a real date. */
const MinRealDateYear = 1900;

/** Format an ISO date, blanking empty, unparseable, or sentinel (`0001-01-01`) values. */
export function formatIsoDateOrBlank(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime()) || date.getUTCFullYear() < MinRealDateYear) {
    return '';
  }
  return formatDateToShortUS(date);
}

/** Date `sortingFn`: compares parsed timestamps (not display strings); missing/unparseable sort oldest. */
export function compareDateStrings(a: string | Date | null | undefined, b: string | Date | null | undefined): number {
  return toComparableTime(a) - toComparableTime(b);
}

function toComparableTime(value: string | Date | null | undefined): number {
  if (value == null) {
    return 0;
  }
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}


/** An hour of the day (0–23) as the start of its bucket, `09:00`. */
export function formatHourOfDay(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/**
  * Format UTC ISO string to clock time (e.g., "2:30 PM")
*/
export function formatDateToClock(date: Date): string {
  return timeFormat.format(date);
}

/**
 * Format a duration in seconds to a human-readable string (e.g., "1h 30m 45s").
 * Always shows at least seconds if the duration is zero.
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const parts: string[] = [];

  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`);

  return parts.join(' ');
}

/**
 * Format a duration in seconds as a clock reading: `m:ss`, rolling over to `h:mm:ss` past one
 * hour (e.g. `61:01` renders as `1:01:01`). Non-finite or negative input renders as `0:00`;
 * fractional seconds are floored.
 */
export function formatClockDuration(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;

  const paddedMinutes = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);
  const paddedSeconds = String(secs).padStart(2, '0');

  return hours > 0 ? `${hours}:${paddedMinutes}:${paddedSeconds}` : `${paddedMinutes}:${paddedSeconds}`;
}


/**
 * Format UTC ISO string to either relative time ("2h 30m ago") or exact time.
 * @param date - UTC ISO string
 * @param locale - Optional locale for formatting
 */
export function formatDateToAgo(date: Date): string {
  const created = date.getTime();
  const now = Date.now();
  const diffMs = now - created;
  if (diffMs < 0) return formatDateToClock(date);

  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffMinutes < 60) {
    return `${Math.max(diffMinutes, 1)}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ${diffMinutes % 60}m ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  if (diffWeeks < 5) {
    return `${diffWeeks}w ago`;
  }
  if (diffMonths < 12) {
    return `${diffMonths}mo ago`;
  }
  return `${diffYears}y ago`;
}
